import {
	Alert,
	Badge,
	Container,
	Group,
	Paper,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconAlertCircle } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
	fetchServerLogList,
	fetchServerLogModules,
} from "@/components/admin/server-log/server-log.api";
import { fill } from "@/components/admin/server-log/server-log.logic";
import {
	EMPTY_FILTERS,
	type ServerLogFilters,
} from "@/components/admin/server-log/server-log.types";
import { ServerLogDrawer } from "@/components/admin/server-log/server-log-drawer";
import { ServerLogFiltersBar } from "@/components/admin/server-log/server-log-filters";
import { ServerLogTable } from "@/components/admin/server-log/server-log-table";
import { useServerLogText } from "@/components/admin/server-log/use-server-log-text";
import { useApiQuery } from "@/hooks/useApiQuery";
import { requireAdminFeature } from "@/middleware/admin-feature-guard";

const SEARCH_DEBOUNCE_MS = 400;
const PAGE_SIZE = 20;
const MODULES_STALE_MS = 60_000;

export const Route = createFileRoute("/admin/server-log")({
	beforeLoad: requireAdminFeature("admin-server-log"),
	component: ServerLogPage,
});

function ServerLogPage() {
	const t = useServerLogText();
	const [filters, setFilters] = useState<ServerLogFilters>(EMPTY_FILTERS);
	const [debouncedQ] = useDebouncedValue(filters.q, SEARCH_DEBOUNCE_MS);
	const [page, setPage] = useState(1);
	const [selected, setSelected] = useState<string | null>(null);
	const effective = { ...filters, q: debouncedQ };

	// biome-ignore lint/correctness/useExhaustiveDependencies: kembali ke halaman 1 setiap filter berubah
	useEffect(
		() => setPage(1),
		[debouncedQ, filters.level, filters.module, filters.status, filters.range],
	);

	const list = useApiQuery(
		["server-log", "list", effective, page],
		() => fetchServerLogList(effective, page),
		{ autoRefresh: true },
	);
	const modules = useApiQuery(
		["server-log", "modules"],
		fetchServerLogModules,
		{
			staleTime: MODULES_STALE_MS,
		},
	);

	return (
		<Container size="xl" py="xl">
			<Group justify="space-between" mb="lg">
				<Stack gap={2}>
					<Title c="orange" order={2}>
						{t.title}
					</Title>
					<Text size="sm" c="dimmed">
						{t.description}
					</Text>
				</Stack>
				<Badge size="lg" variant="outline">
					{fill(t.total, { n: list.data?.total ?? 0 })}
				</Badge>
			</Group>

			<Paper p="md" withBorder radius="md" mb="md">
				<ServerLogFiltersBar
					value={filters}
					modules={modules.data ?? []}
					onChange={setFilters}
				/>
			</Paper>

			{list.isError && (
				<Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
					{t.loadFailed}
				</Alert>
			)}
			<Paper p="md" withBorder radius="md">
				<ServerLogTable
					rows={list.data?.data ?? []}
					total={list.data?.total ?? 0}
					page={page}
					pageSize={PAGE_SIZE}
					onPage={setPage}
					onSelect={setSelected}
				/>
			</Paper>

			<ServerLogDrawer groupId={selected} onClose={() => setSelected(null)} />
		</Container>
	);
}
